import { useState, useMemo } from "react"
import {Box, Cylinder, Square, ArrowRight, Save, Trash2, Bookmark} from 'lucide-react'
import toast from "react-hot-toast"
import type { Unit } from "@/data/oilDatabase"
import type { MoldDimensions, MoldPreset, MoldShape } from "@/hooks/useMoldPresets"

interface MoldVolumeCalculatorProps {
  unit: Unit
  setBatchSize: (v: number) => void
  presets: MoldPreset[]
  presetsLoading: boolean
  presetsSaving: boolean
  canSavePresets: boolean
  onSavePreset: (dimensions: MoldDimensions) => Promise<boolean>
  onDeletePreset: (id: string) => Promise<void>
  onRequestSignIn: () => void
}

// Industry-standard conversion: 1 cubic inch ≈ 1.8 oz of soap batter ≈ 51.03 grams
// Oils typically make up ~70% of a CP soap batch (rest is lye + water + fragrance)
const CUBIC_INCH_TO_OUNCES = 1.8
const CUBIC_INCH_TO_GRAMS = 51.03
const OIL_RATIO_OF_BATCH = 0.7

export function MoldVolumeCalculator({
  unit,
  setBatchSize,
  presets,
  presetsLoading,
  presetsSaving,
  canSavePresets,
  onSavePreset,
  onDeletePreset,
  onRequestSignIn,
}: MoldVolumeCalculatorProps) {
  const [shape, setShape] = useState<MoldShape>("rectangular")
  const [length, setLength] = useState(10)
  const [width, setWidth] = useState(3.5)
  const [height, setHeight] = useState(3.5)
  const [diameter, setDiameter] = useState(3)
  const [presetName, setPresetName] = useState("")
  const [showSaveForm, setShowSaveForm] = useState(false)

  const applyPreset = (preset: MoldPreset) => {
    setShape(preset.shape)
    setLength(preset.length)
    setWidth(preset.width)
    setHeight(preset.height)
    setDiameter(preset.diameter)
    toast.success(`Loaded "${preset.name}"`)
  }

  const handleSavePreset = async () => {
    const trimmed = presetName.trim()
    if (!trimmed) {
      toast.error("Give the mold a name first")
      return
    }
    const saved = await onSavePreset({
      name: trimmed,
      shape,
      length,
      width,
      height,
      diameter,
      unit,
    })
    if (saved) {
      setPresetName("")
      setShowSaveForm(false)
    }
  }

  const { volumeCubicInches, totalBatchWeight, suggestedOilWeight } = useMemo(() => {
    let vol = 0
    if (shape === "rectangular") {
      vol = length * width * height
    } else if (shape === "round") {
      const radius = diameter / 2
      vol = Math.PI * radius * radius * height
    } else {
      // slab - same math as rectangular but different presentation
      vol = length * width * height
    }

    const conversion = unit === "grams" ? CUBIC_INCH_TO_GRAMS : CUBIC_INCH_TO_OUNCES
    const batchWeight = vol * conversion
    const oilWeight = batchWeight * OIL_RATIO_OF_BATCH

    return {
      volumeCubicInches: vol,
      totalBatchWeight: batchWeight,
      suggestedOilWeight: oilWeight,
    }
  }, [shape, length, width, height, diameter, unit])

  const handleUseForBatch = () => {
    if (suggestedOilWeight <= 0) {
      toast.error("Enter valid mold dimensions first")
      return
    }
    setBatchSize(Math.round(suggestedOilWeight))
    toast.success(`Batch size set to ${Math.round(suggestedOilWeight)} ${unit}`)
  }

  const ShapeButton = ({ value, icon: Icon, label }: { value: MoldShape; icon: typeof Box; label: string }) => (
    <button
      onClick={() => setShape(value)}
      className={`flex flex-col items-center gap-1 py-3 px-2 rounded-lg font-medium transition-all text-xs ${
        shape === value
          ? "bg-emerald-600 text-white shadow"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  )

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
        <Box size={20} />
        Mold Volume Calculator
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Dimensions in inches · Auto-calculates batch size
      </p>

      {/* Saved molds */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Bookmark size={14} />
            My Molds
          </label>
          {presets.length > 0 && (
            <span className="text-[11px] text-gray-400">{presets.length} saved</span>
          )}
        </div>

        {!canSavePresets ? (
          <button
            onClick={onRequestSignIn}
            className="w-full text-left text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 hover:bg-emerald-100 transition-colors"
          >
            Sign in to save your mold dimensions
          </button>
        ) : (
          <>
            {presetsLoading ? (
              <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />
            ) : presets.length === 0 ? (
              <p className="text-xs text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-3 py-2">
                No saved molds yet. Save one below for next time.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {presets.map((preset) => (
                  <div
                    key={preset._id}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5"
                  >
                    <button
                      onClick={() => applyPreset(preset)}
                      className="flex-1 text-left text-xs font-medium text-gray-800 hover:text-emerald-700 truncate"
                      title={`Load ${preset.name}`}
                    >
                      {preset.name}
                      <span className="block text-[10px] text-gray-400 font-normal">
                        {preset.shape === "round"
                          ? `Ø${preset.diameter} × ${preset.height} in`
                          : `${preset.length} × ${preset.width} × ${preset.height} in`}
                      </span>
                    </button>
                    <button
                      onClick={() => preset._id && void onDeletePreset(preset._id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                      aria-label={`Delete ${preset.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showSaveForm ? (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSavePreset()
                  }}
                  placeholder="Mold name, e.g. Loaf 10in"
                  className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleSavePreset()}
                    disabled={presetsSaving}
                    className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-xs font-semibold rounded-lg transition-all"
                  >
                    {presetsSaving ? "Saving..." : "Save Mold"}
                  </button>
                  <button
                    onClick={() => {
                      setShowSaveForm(false)
                      setPresetName("")
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSaveForm(true)}
                className="mt-2 w-full py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                <Save size={13} />
                Save these dimensions
              </button>
            )}
          </>
        )}
      </div>

      {/* Shape Selection */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Mold Shape</label>
        <div className="grid grid-cols-3 gap-2">
          <ShapeButton value="rectangular" icon={Box} label="Loaf" />
          <ShapeButton value="round" icon={Cylinder} label="Round" />
          <ShapeButton value="slab" icon={Square} label="Slab" />
        </div>
      </div>

      {/* Dimension Inputs */}
      <div className="space-y-3 mb-4">
        {shape === "round" ? (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Diameter (in)</label>
              <input
                type="number"
                value={diameter}
                onChange={(e) => setDiameter(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Height (in)</label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                min="0"
                step="0.1"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Length (in)</label>
              <input
                type="number"
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Width (in)</label>
              <input
                type="number"
                value={width}
                onChange={(e) => setWidth(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {shape === "slab" ? "Depth" : "Height"} (in)
              </label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                min="0"
                step="0.1"
              />
            </div>
          </>
        )}
      </div>

      {/* Results */}
      {volumeCubicInches > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-3">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Volume:</span>
              <span className="font-semibold text-gray-900">
                {volumeCubicInches.toFixed(2)} in³
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Batter:</span>
              <span className="font-semibold text-gray-900">
                {totalBatchWeight.toFixed(1)} {unit}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-emerald-200">
              <span className="text-gray-900 font-semibold">Suggested Oils:</span>
              <span className="font-bold text-emerald-700">
                {suggestedOilWeight.toFixed(1)} {unit}
              </span>
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-2 leading-tight">
            Oils are ~70% of batch (lye + liquid + fragrance ≈ 30%)
          </p>
        </div>
      )}

      {/* Apply Button */}
      <button
        onClick={handleUseForBatch}
        disabled={suggestedOilWeight <= 0}
        className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 text-sm shadow-sm"
      >
        Use for Batch Size
        <ArrowRight size={16} />
      </button>
    </div>
  )
}
